import { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import AnimationRevealPage from "../helpers/AnimationRevealPage.js";
import tw from "twin.macro";
import styled, { css } from "styled-components";
import Header from "../Components/headers/PageHeader";
import ContactRightColum from "../Components/Forms/ContactFormCol";
import { Helmet } from "react-helmet";

// --- REDESIGNED STYLED COMPONENTS ---

const Container = tw.div`max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8`;
const Section = tw.section`py-4`;

// New Layout Structure
const PageLayout = tw.div`flex flex-col lg:flex-row gap-12 mt-8`;
const MainColumn = tw.div`flex-1 min-w-0`; // min-w-0 prevents flex items from overflowing
const SidebarColumn = tw.aside`hidden lg:block w-72 flex-shrink-0 text-left`;

const StickySidebar = styled.div`
  ${tw`sticky top-0 self-start p-6 bg-gray-100 rounded-2xl border border-gray-100 shadow-sm`}
`;

const ToCTitle = tw.h4`text-xs font-bold uppercase tracking-widest text-customPurple mb-4`;
const ToCLink = styled.a`
  ${tw`block text-sm py-2 text-gray-800 hover:text-customPinkLight transition-colors border-l-2 border-black`}
  ${props => props.active && tw`border-customPurple text-customPurple font-medium`}
  &:hover {
    ${tw`border-gray-300`}
  }
`;

const HeroContainer = styled.div`
  ${tw`relative bg-primary-500 text-white py-20 md:py-32 rounded-3xl overflow-hidden mb-12`}
  background-image: url(${(props) => props.imageSrc || "https://via.placeholder.com/1600x600"});
  background-size: cover;
  background-position: center;
`;
const HeroOverlay = tw.div`absolute inset-0 bg-black opacity-15`;
const HeroContent = tw.div`relative z-10 text-center max-w-4xl mx-auto px-4`;
const HeroHeading = tw.h1`text-4xl md:text-6xl font-extrabold leading-tight mb-6`;
const HeroSubheading = tw.p`text-lg md:text-2xl mb-8 opacity-25`;
const HeroButton = tw.button`px-8 py-4 bg-white text-primary-600 font-bold rounded-full hover:bg-gray-100 transition shadow-lg`;

const Breadcrumbs = tw.nav`text-sm font-medium mb-6 py-2 border-b border-gray-100`;
const BreadcrumbLink = tw(Link)`text-gray-500 hover:text-primary-600 transition-colors`;

const ContentSection = styled.div`
  ${tw`mx-auto text-left`}
  h1, h2, h3, h4, h5, h6 {
    ${tw`font-bold text-gray-900 tracking-tight scroll-mt-24`} // scroll-mt accounts for sticky headers
  }
  h1 { ${tw`text-4xl md:text-5xl mt-12 mb-6`} }
  h2 { ${tw`text-3xl md:text-4xl mt-10 mb-5 pb-2 border-b border-gray-100`} }
  h3 { ${tw`text-2xl md:text-3xl mt-8 mb-4`} }
  
  p { ${tw`text-lg text-gray-700 mb-6 leading-relaxed`} }
  
  ul, ol { ${tw`pl-6 mb-8 space-y-3`} }
  ul { ${tw`list-disc text-primary-500`} }
  ol { ${tw`list-decimal text-primary-500`} }
  
  ul li, ol li { ${tw`text-lg text-gray-700`} }
  
  blockquote {
    ${tw`border-l-4 border-primary-500 pl-6 py-2 italic text-xl text-gray-600 my-8 bg-gray-100 rounded-r-lg`}
  }
  
  img { ${tw`rounded-2xl shadow-md my-8 w-full h-auto`} }
`;

const ContentBlock = styled.div`
  ${tw`rounded-2xl p-6 md:p-8 my-8`}
  ${({ variant }) => {
    switch (variant) {
      case "highlighted": return tw`bg-blue-100 border-l-8 border-primary-600`;
      case "boxed": return tw`bg-white shadow-xl border border-gray-100`;
      default: return tw`bg-transparent p-0`;
    }
  }}
`;

const Banner = styled.div`
  ${tw`p-8 my-10 rounded-2xl text-center font-semibold text-lg shadow-sm`}
  background-color: ${(props) => {
    switch (props.variant) {
      case "primary": return "#3b82f6";
      case "secondary": return "#6b7280";
      case "info": return "#06b6d4";
      case "warning": return "#f59e0b";
      default: return "#3b82f6";
    }
  }};
  color: white;
  ${props => props.variant === "warning" && tw`text-gray-900`}
`;

const CallToAction = tw.div`text-center my-12`;
const CtaButton = tw.a`inline-block px-10 py-4 bg-primary-600 text-white font-bold rounded-xl hover:bg-primary-700 transition transform hover:-translate-y-1 shadow-lg`;

const CardSectionContainer = styled.div`
  ${tw`my-12`}
  ${({ layout }) => {
    switch (layout) {
      case "grid-1": return tw`grid grid-cols-1 gap-8`;
      case "grid-2": return tw`grid grid-cols-1 md:grid-cols-2 gap-8`;
      case "grid-3": return tw`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8`;
      case "grid-4": return tw`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6`;
      default: return tw`grid grid-cols-1 md:grid-cols-3 gap-8`;
    }
  }}
`;

const Card = tw.div`flex flex-col p-0 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden`;
const CardImage = tw.img`w-full h-56 object-cover`;
const CardBody = tw.div`p-6 flex-1 flex flex-col`;
const CardTitle = tw.h3`text-xl font-bold mb-3 text-gray-900`;
const CardDescription = tw.div`text-gray-600 mb-6 flex-1`;

const TableWrapper = tw.div`w-full overflow-x-auto my-10 rounded-2xl border border-gray-200 shadow-sm`;
const TableSection = styled.table(({ variant }) => [
  tw`min-w-full border-collapse bg-white`,
  variant === "striped" && tw`[&>tbody>tr:nth-child(even)]:bg-gray-100`,
  variant === "bordered" && css`
    & th, & td { border: 1px solid #e5e7eb; }
  `,
]);
const TableHeader = tw.th`p-5 text-left font-bold text-gray-900 bg-gray-100 border-b border-gray-200 uppercase text-xs tracking-wider`;
const TableCell = tw.td`p-5 text-gray-700 border-b border-gray-100`;

const GridLayoutContainer = styled.div`
  ${tw`my-12 grid gap-8`}
  ${({ columns }) => {
    switch (columns) {
      case "1": return tw`grid-cols-1`;
      case "2": return tw`grid-cols-1 md:grid-cols-2`;
      case "3": return tw`grid-cols-1 md:grid-cols-3`;
      case "4": return tw`grid-cols-1 md:grid-cols-2 lg:grid-cols-4`;
      default: return tw`grid-cols-1 md:grid-cols-2`;
    }
  }}
`;

const AccordionSection = tw.div`my-12 space-y-4`;
const AccordionItem = tw.div`border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm`;
const AccordionButton = tw.button`w-full text-left p-5 font-bold text-gray-800 hover:bg-gray-100 flex justify-between items-center transition-colors`;
const AccordionContent = styled.div`
  ${tw`p-6 bg-gray-100 border-t border-gray-100`}
  ${({ isOpen }) => (isOpen ? tw`block` : tw`hidden`)}
`;

const ContactSection = tw(Section)`bg-gray-100 rounded-3xl p-8 md:p-16 my-16`;
const Loading = tw.div`text-center py-32 text-xl font-medium text-gray-500`;
const ErrorMessage = tw.div`text-center py-32 text-red-600 text-xl font-bold`;

// --- HELPER COMPONENTS ---

const MainContainer = tw.main`grid h-[70vh] place-items-center bg-white px-6 py-24 sm:py-32 lg:px-8`;
const SubContainer = tw.div`text-center`;
const Para = tw.p`text-base font-semibold text-customPurple`;
const HOne = tw.h1`mt-4 text-5xl font-semibold tracking-tight text-balance text-gray-900 sm:text-5xl`;
const ParaTwo = tw.p`mt-6 text-lg font-medium text-pretty text-gray-500 sm:text-xl/8`;
const SuperSub = tw.div`mt-10 flex items-center justify-center gap-x-6`;
const Links = tw(Link)`rounded-md bg-customPurple px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-customPinkLight`;

const NotFound = () => (
  <MainContainer>
    <SubContainer>
      <Para>404</Para>
      <HOne>Page not found</HOne>
      <ParaTwo>Sorry, we couldn’t find the page you’re looking for.</ParaTwo>
      <SuperSub>
        <Links to="/">Go back home</Links>
        <Links to="/contact" tw="text-sm font-semibold text-gray-900">
          Contact support <span aria-hidden="true">→</span>
        </Links>
      </SuperSub>
    </SubContainer>
  </MainContainer>
);

// --- MAIN COMPONENT ---

const DynamicPage = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openAccordions, setOpenAccordions] = useState({});

  const websiteDomain = process.env.REACT_APP_WEBSITE_DOMAIN || "milaajbrandset.com";
  const API_URL = process.env.REACT_APP_CMS_API_URL || "http://localhost:3000/api";

  useEffect(() => {
    const fetchPage = async () => {
      try {
        setLoading(true);
        const url = `${API_URL}/api/pages/by-slug/${encodeURIComponent(websiteDomain)}/${slug}`;
        const response = await axios.get(url, {
          params: { depth: 1, draft: false },
          headers: { Accept: "application/json" },
        });
        if (!response.data) throw new Error("No page data returned");
        setPage(response.data);
        if (response.data.error === "Website not found") setError("not-found");
        setLoading(false);
      } catch (err) {
        if (err.response?.status === 404 || err.message === "No page data returned") {
          setError("not-found");
        } else {
          setError("Unable to load the page. Please try again later.");
        }
        setLoading(false);
      }
    };
    fetchPage();
  }, [slug, websiteDomain, API_URL]);

  // Extract headings for Table of Contents
  const tableOfContents = useMemo(() => {
    if (!page?.content?.root?.children) return [];
    
    return page.content.root.children
      .filter(node => node.type === "heading" && (node.tag === "h2" || node.tag === "h3"))
      .map(node => {
        const text = node.children?.map(child => child.text).join("") || "";
        const id = text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
        return { text, id, level: node.tag };
      });
  }, [page]);

  const toggleAccordion = (index) => {
    setOpenAccordions((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const parseStyle = (styleString) => {
    if (!styleString || typeof styleString !== "string") return {};
    return styleString
      .split(";")
      .filter((rule) => rule.trim())
      .reduce((styles, rule) => {
        const [property, value] = rule.split(":").map((s) => s.trim());
        if (property && value && !property.startsWith("--")) {
          const camelCaseProp = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
          styles[camelCaseProp] = value;
        }
        return styles;
      }, {});
  };

  const renderRichText = (richText) => {
    if (!richText?.root?.children) return null;

    const renderNode = (node, index) => {
      let style = node.style ? parseStyle(node.style) : {};

      switch (node.type) {
        case "heading":
          const HeadingTag = node.tag || "h2";
          const headingText = node.children?.map(child => child.text).join("") || "";
          const headingId = headingText.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
          return (
            <HeadingTag key={index} id={headingId} style={style}>
              {node.children?.map((child, i) => renderChild(child, i))}
            </HeadingTag>
          );

        case "paragraph":
          return (
            <p key={index} style={style}>
              {node.children?.map((child, i) => renderChild(child, i))}
            </p>
          );

        case "list":
          const ListTag = node.listType === "bullet" || !node.listType ? "ul" : "ol";
          return (
            <ListTag key={index} style={style}>
              {node.children?.map((item, i) => (
                <li key={i}>
                  {item.children?.map((child, j) => renderChild(child, j))}
                </li>
              ))}
            </ListTag>
          );

        case "quote":
          return (
            <blockquote key={index} style={style}>
              {node.children?.map((child, i) => renderChild(child, i))}
            </blockquote>
          );

        case "block":
          switch (node.fields?.blockType) {
            case "banner":
              return (
                <Banner key={index} variant={node.fields.style}>
                  <p>{node.fields.title}</p>
                </Banner>
              );
            case "callToAction":
              return (
                <CallToAction key={index}>
                  <CtaButton
                    href={node.fields.link}
                    target={node.fields.openInNewTab ? "_blank" : "_self"}
                    rel="noopener noreferrer"
                  >
                    {node.fields.text}
                  </CtaButton>
                </CallToAction>
              );
            case "hero":
              return (
                <HeroContainer
                  key={index}
                  imageSrc={node.fields.backgroundImage?.url ? `${API_URL}${node.fields.backgroundImage.url}` : null}
                >
                  <HeroOverlay />
                  <HeroContent>
                    <HeroHeading>{node.fields.headline}</HeroHeading>
                    {node.fields.subheadline && <HeroSubheading>{node.fields.subheadline}</HeroSubheading>}
                    {node.fields.cta?.text && (
                      <HeroButton as={Link} to={node.fields.cta.link || "/get-started"}>
                        {node.fields.cta.text}
                      </HeroButton>
                    )}
                  </HeroContent>
                </HeroContainer>
              );
            case "cardSection":
              return (
                <CardSectionContainer key={index} layout={node.fields.layout}>
                  {node.fields.cards?.map((card, cardIndex) => (
                    <Card key={cardIndex}>
                      {card.image?.url && (
                        <CardImage src={`${API_URL}${card.image.url}`} alt={card.image.alt || ""} />
                      )}
                      <CardBody>
                        <CardTitle>{card.title}</CardTitle>
                        {card.description && (
                          <CardDescription>{renderRichText(card.description)}</CardDescription>
                        )}
                        {card.cta?.text && (
                          <CtaButton
                            href={card.cta.link}
                            target={card.cta.openInNewTab ? "_blank" : "_self"}
                            rel="noopener noreferrer"
                            tw="text-sm px-6 py-3"
                          >
                            {card.cta.text}
                          </CtaButton>
                        )}
                      </CardBody>
                    </Card>
                  ))}
                </CardSectionContainer>
              );
            case "tableSection":
              return (
                <TableWrapper key={index}>
                  <TableSection variant={node?.fields?.style}>
                    <thead>
                      <tr>
                        {node.fields.columns?.map((column, colIndex) => (
                          <TableHeader key={colIndex} style={{ textAlign: column.align || "left" }}>
                            {column.header}
                          </TableHeader>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {node.fields.rows?.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {row.cells?.map((cell, cellIndex) => (
                            <TableCell
                              key={cellIndex}
                              style={{ textAlign: node.fields.columns[cellIndex]?.align || "left" }}
                            >
                              {renderRichText(cell.content)}
                            </TableCell>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </TableSection>
                </TableWrapper>
              );
            case "gridLayout":
              return (
                <div key={index}>
                  {node.fields.title && <h2 tw="text-2xl font-semibold mb-4">{node.fields.title}</h2>}
                  <GridLayoutContainer columns={node.fields.columns} gap={node.fields.gap}>
                    {node.fields.contentBlocks?.map((block, blockIndex) =>
                      block?.blockType ? (
                        <div key={blockIndex}>
                          {renderNode({ type: "block", fields: block }, blockIndex)}
                        </div>
                      ) : null
                    )}
                  </GridLayoutContainer>
                </div>
              );
            case "accordionSection":
              return (
                <AccordionSection key={index}>
                  {node.fields.title && <h2 tw="text-2xl font-semibold mb-6">{node.fields.title}</h2>}
                  {node.fields.items?.map((item, itemIndex) => (
                    <AccordionItem key={itemIndex}>
                      <AccordionButton onClick={() => toggleAccordion(`${index}-${itemIndex}`)}>
                        {item.title}
                        <span tw="text-xl">{openAccordions[`${index}-${itemIndex}`] ? "−" : "+"}</span>
                      </AccordionButton>
                      <AccordionContent isOpen={openAccordions[`${index}-${itemIndex}`]}>
                        {renderRichText(item.content)}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </AccordionSection>
              );
            case "contentSection":
              return (
                <ContentBlock key={index} variant={node?.fields?.style}>
                  {node.fields.title && <h2 tw="text-2xl font-semibold mb-4">{node.fields.title}</h2>}
                  {renderRichText(node.fields.content)}
                </ContentBlock>
              );
            default:
              return null;
          }

        case "upload":
          return (
            <img
              key={index}
              src={`${API_URL}${node?.value?.url}`}
              alt={node.fields?.alt || ""}
              style={style}
            />
          );

        default:
          return null;
      }
    };

    const renderChild = (child, index) => {
      let style = child.style ? parseStyle(child.style) : {};
      if (child.format & 1) style.fontWeight = "bold";
      if (child.format & 2) style.fontStyle = "italic";
      if (child.format & 4) style.textDecoration = "underline";
      if (child.format & 8) style.textDecoration = "line-through";
      
      if (child.type === "text") return <span key={index} style={style}>{child.text}</span>;
      if (child.type === "link") {
        return (
          <a
            key={index}
            href={child.fields?.href}
            target={child.fields?.openInNewTab ? "_blank" : "_self"}
            rel={child.fields?.openInNewTab ? "noopener noreferrer" : undefined}
            style={style}
          >
            {child.children?.map((grandchild, i) => renderChild(grandchild, i))}
          </a>
        );
      }
      if (child.type === "upload") {
        return (
          <img
            key={index}
            src={`${API_URL}${child.fields?.url}`}
            alt={child.fields?.alt || ""}
            style={style}
          />
        );
      }
      return null;
    };

    return richText.root.children.map((node, index) => renderNode(node, index));
  };

  if (loading) return <Loading>Loading...</Loading>;
  if (error === "not-found" || !page) return <NotFound />;
  if (error) return <ErrorMessage>Error: {error}</ErrorMessage>;

  const structuredData = page.seo?.schema || {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: page.title,
    url: `https://${websiteDomain}/${slug}`,
    description: page.seo?.metaDescription || "",
  };

  return (
    <AnimationRevealPage>
      <Helmet>
        <title>{page.seo?.metaTitle || page.title}</title>
        <meta name="description" content={page.seo?.metaDescription || ""} />
        <meta name="keywords" content={page.seo?.keywords || ""} />
        <meta name="robots" content={page.seo?.robots || "index,follow"} />
        {page.seo?.canonicalUrl && <link rel="canonical" href={page.seo.canonicalUrl} />}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>
      
      <Header from={page.title} />
      
      <Container>
        <PageLayout>
          <MainColumn>
            <Breadcrumbs aria-label="Breadcrumb">
              <ol tw="flex flex-wrap gap-2">
                {page.breadcrumbs?.map((crumb, index) => (
                  <li key={index} tw="inline-flex items-center">
                    <BreadcrumbLink to={crumb.url}>{crumb.label}</BreadcrumbLink>
                    {index < page.breadcrumbs.length - 1 && <span tw="mx-2 text-gray-300">/</span>}
                  </li>
                ))}
              </ol>
            </Breadcrumbs>

            <Section>
              <ContentSection>{renderRichText(page.content)}</ContentSection>
            </Section>

            {page.showContactForm && (
              <ContactSection>
                <h2 tw="text-3xl font-bold text-center mb-8 text-gray-900">Get in Touch</h2>
                <div tw="max-w-xl mx-auto bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
                  <ContactRightColum />
                </div>
              </ContactSection>
            )}
          </MainColumn>

          <SidebarColumn>
            <StickySidebar>
              {tableOfContents.length > 0 && (
                <>
                  <ToCTitle>On this page</ToCTitle>
                  <nav tw="mb-8">
                    {tableOfContents.map((item, idx) => (
                      <ToCLink 
                        key={idx} 
                        href={`#${item.id}`}
                        style={{ paddingLeft: item.level === "h3" ? "1.5rem" : "1rem" }}
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById(item.id)?.scrollIntoView({ behavior: "smooth" });
                        }}
                      >
                        {item.text}
                      </ToCLink>
                    ))}
                  </nav>
                </>
              )}
            </StickySidebar>
          </SidebarColumn>
        </PageLayout>
      </Container>
    </AnimationRevealPage>
  );
};

export default DynamicPage;